<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { RouterLink } from "vue-router"
import { useI18n } from "vue-i18n"
import { useConfigStore } from "@core/stores/config.store"
import { storeToRefs } from "pinia"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            linkWebsite: "Aller sur Homeys",
            phone: "01 83 62 35 79",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
export type FooterLoginLink = {
    text: string
    label: string
    link_to: string
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        links?: FooterLoginLink[]
        forgetPassword?: boolean
    }>(),
    {
        links: () => [],
        forgetPassword: false,
    }
)

// ================================
// COMPOSABLES
// ================================
const configStore = useConfigStore()
const { isDefaultScope } = storeToRefs(configStore)

// ================================
// COMPUTED
// ================================
/** Liens affichés dans le footer (inscription, première connexion, etc.) */
const getLinks = computed(() => props.links)
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div class="footer-login flex flex-col items-center w-full gap-4">
        <!-- ================================ -->
        <!-- LISTE DE LIENS -->
        <!-- ================================ -->
        <div
            v-if="getLinks.length"
            class="flex flex-col items-center gap-2 w-full"
        >
            <div
                v-for="(link, index) in getLinks"
                :key="index"
                class="flex sm:flex-row flex-col items-center justify-between w-full"
            >
                <p class="text-sm">{{ link.text }}</p>
                <BaseButton variant="link" size="small">
                    <RouterLink :to="link.link_to">
                        {{ link.label }}
                    </RouterLink>
                </BaseButton>
            </div>
        </div>
        <!-- ================================ -->
        <!-- BLOC SITE / TÉLÉPHONE / SUPPORT (scope Homeys) -->
        <!-- ================================ -->
        <div v-if="isDefaultScope" class="flex flex-col gap-2">
            <BaseButton
                variant="link"
                as="a"
                href="https://www.homeys.fr/"
                size="small"
                target="_blank"
                rel="noopener"
                class="text-xs whitespace-nowrap"
                :label="t('linkWebsite')"
            />
            <div class="grid grid-cols-1 gap-2 mx-auto sm:grid-cols-2">
                <BaseButton
                    as="a"
                    variant="link"
                    href="tel:0183623579"
                    class="text-xs whitespace-nowrap"
                    size="small"
                    target="_blank"
                    rel="noopener"
                    :label="t('phone')"
                />
                <BaseButton
                    variant="link"
                    as="a"
                    href="mailto:support@homeys.io"
                    target="_blank"
                    class="text-xs"
                    rel="noopener"
                    size="small"
                    label="support@homeys.io"
                />
            </div>
        </div>
    </div>
</template>
